import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BasicFormErrors, BasicFormErrorsEmpty } from './types'
import DatePickerSingle from 'components/Form/DatePickerSingle'
import FormColumn from 'components/Form/FormColumn'
import FormRow from 'components/Form/FormRow'
import Input, { InputType, OnInputChangeFn } from 'components/Form/Input'
import SelectAsync from 'components/Form/SelectAsync'
import {
  BasicsValues,
  basicsValuesDefault,
  CampaignFormContext,
} from 'contexts/CampaignFormContext'
import usePrevious from 'hooks/usePrevious'
import VariableUtils from 'utils/variable'
import { useNavigate, useParams } from 'react-router-dom'
import { ReactRouterParams } from 'types/various'
import { DATE_PLACEHOLDER } from 'constant'
import { AppContext } from 'contexts/AppContext'
import { CAMPAIGN_FORM } from 'constant/authorization'
import DateUtils from 'utils/date'
import debounce from 'lodash/debounce'
import { Campaign, CampaignStatus } from 'types/campaign'
import './BasicsForm.scss'
import { BaseRole } from '../../../../types/user'
import { useSelector } from 'react-redux'
import { campaignFormActions } from '../../store/campaign-form-slice'
import { RootState, store } from '../../../../store'
import BasicsFormService from './services/basics-form.service'
import { AgencyType } from '../../../../types/agency'
import { CampaignPermissionType, FeatureName } from 'types/features'
import { SelectAsyncValue } from '../../../../components/Form/SelectAsync/types'
import api from 'api'

const DEBOUNCE_POST_DELAY = 1000 // [ms]

const TagCampaignsTooltip: React.FC<{ tag: string }> = ({ tag }) => {
  const { t } = useTranslation()
  const cachedCampaigns = useSelector((state: RootState) => state.campaignForm.tagCampaigns[tag])
  const [loading, setLoading] = useState(!cachedCampaigns)

  useEffect(() => {
    if (cachedCampaigns) return

    api.campaign
      .getCampaignsByTag(tag)
      .then(res => {
        store.dispatch(
          campaignFormActions.setTagCampaigns({
            tag,
            campaigns: res.data.campaigns.nodes,
          })
        )
      })
      .catch(() => {
        store.dispatch(campaignFormActions.setTagCampaigns({ tag, campaigns: [] }))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [tag, cachedCampaigns])

  const campaigns = cachedCampaigns ?? []

  return (
    <div className='BasicsForm__tag-tooltip'>
      <div className='BasicsForm__tag-tooltip-header'>
        {t('form.campaign.basics.campaignsWithTag', { tag })}
      </div>
      {loading ? (
        <div>{t('common.loading')}</div>
      ) : campaigns.length > 0 ? (
        <ul className='BasicsForm__tag-tooltip-list'>
          {campaigns.map(campaign => (
            <li key={campaign.id}>{campaign.name}</li>
          ))}
        </ul>
      ) : (
        <div>{t('form.noOptions')}</div>
      )}
    </div>
  )
}

const BasicsForm: React.FC = () => {
  const { t } = useTranslation()
  const { allowedFor, userData } = useContext(AppContext)
  const navigate = useNavigate()
  const { triggerFetchCampaign, setTriggerFetchCampaign } = useContext(CampaignFormContext)
  const { id: urlId } = useParams<ReactRouterParams>()
  const [errors, setErrors] = useState<BasicFormErrors>(BasicFormErrorsEmpty)
  const [fetchCompleted, setFetchCompleted] = useState<boolean>(false)
  const [canUpdate, setCanUpdate] = useState<boolean>(false)
  const { basicsValues, detailsValues } = useSelector((state: RootState) => state.campaignForm.form)
  const previousValues = usePrevious<BasicsValues>(basicsValues)

  const campaignId = basicsValues.campaignId
  const campaignStatus = basicsValues.status

  const onInputChange: OnInputChangeFn = (input, id, extraFields): void => {
    store.dispatch(
      campaignFormActions.updateBasicsValues({
        ...basicsValues,
        [id ?? '']: input,
        ...(extraFields ? Object.assign({}, ...extraFields.map(e => ({ [e.id]: e.value }))) : {}),
      })
    )
  }

  const debouncedHandleSubmit = useCallback(
    debounce(BasicsFormService.handleSubmit, DEBOUNCE_POST_DELAY),
    []
  )

  const debouncedHandleUpdate = useCallback(
    debounce(BasicsFormService.handleUpdate, DEBOUNCE_POST_DELAY),
    []
  )

  const fetchValues = (campaignId: Campaign['id']): void =>
    BasicsFormService.fetchValues(
      campaignId,
      basicsValues,
      setFetchCompleted,
      setTriggerFetchCampaign,
      allowedFor,
      userData.user?.roles,
      navigate,
      userData.withAllocatedTime,
      t
    )

  const isNotAllowedDate = (
    day: Date,
    dateTo: string,
    dateFrom: string,
    isDateFromDatePicker: boolean,
    reservationDate?: Date | null
  ): boolean => {
    if (reservationDate) {
      return DateUtils.isBeforeDay(day, new Date()) || DateUtils.isBeforeDay(day, reservationDate)
    }
    if (DateUtils.isBeforeDay(day, new Date())) {
      return true
    }
    if (isDateFromDatePicker) {
      return day > new Date(dateTo)
    }

    return day < new Date(dateFrom)
  }

  const disabledSupervisors = (): boolean => {
    if (userData.user?.roles.some(role => role.baseRole === BaseRole.SUPPORT)) {
      return false
    }

    return basicsValues.agency === null || !basicsValues.campaignId
  }
  const disabledAgency = (): boolean => campaignStatus === CampaignStatus.ACTIVE

  useEffect(() => {
    if (urlId && triggerFetchCampaign) {
      fetchValues(urlId)
    }
  }, [triggerFetchCampaign])

  useEffect(() => {
    if (urlId && !fetchCompleted) {
      fetchValues(urlId)
    } else {
      setFetchCompleted(true)
    }
  }, [urlId, fetchCompleted])

  useEffect(() => {
    if (previousValues && basicsValues) {
      const notTouched = VariableUtils.isDeepEqual(
        { ...previousValues, campaignId: null, brainState: null },
        { ...basicsValues, campaignId: null, brainState: null }
      )
      const differentThanDefaults = !VariableUtils.isDeepEqual(previousValues, basicsValuesDefault)

      if (notTouched) {
        setCanUpdate(false)
      } else {
        BasicsFormService.validate(
          basicsValues,
          allowedFor,
          setErrors,
          errors,
          t,
          basicsValues.dealId
        )

        if (fetchCompleted && differentThanDefaults) {
          setCanUpdate(true)
        }
      }
    }
  }, [previousValues, basicsValues, fetchCompleted])

  useEffect(() => {
    if (!BasicsFormService.hasErrors(errors) && fetchCompleted) {
      if (!campaignId) {
        void debouncedHandleSubmit(
          basicsValues,
          onInputChange,
          navigate,
          detailsValues,
          setTriggerFetchCampaign,
          userData.user?.roles
        )
      } else if (canUpdate) {
        debouncedHandleUpdate(basicsValues, t, userData.user?.roles)
      }
    }
  }, [debouncedHandleSubmit, debouncedHandleUpdate, errors, canUpdate])

  return (
    <div className='BasicsForm'>
      <FormRow className='BasicsForm__row'>
        <FormColumn>
          <Input
            id='name'
            title={t('form.campaign.basics.name')}
            placeholder={t('form.campaign.basics.namePlaceholder')}
            value={basicsValues.name}
            onChange={(input, id): void => {
              if (userData.withAllocatedTime) {
                onInputChange(input, id, [{ id: 'briefName', value: input }])
                return
              }

              onInputChange(input, id)
            }}
            errors={errors?.name}
            disabled={!allowedFor({ template: CAMPAIGN_FORM.NAME, status: campaignStatus })}
          />
        </FormColumn>
      </FormRow>

      {!userData.withAllocatedTime && (
        <FormRow className='BasicsForm__row'>
          <FormColumn>
            <Input
              id='briefName'
              title={t('common.briefName')}
              placeholder={t('form.campaign.basics.briefNamePlaceholder')}
              value={basicsValues.briefName}
              onChange={onInputChange}
              errors={errors?.briefName}
              disabled={!allowedFor({ template: CAMPAIGN_FORM.BRIEF_NAME, status: campaignStatus })}
            />
          </FormColumn>
        </FormRow>
      )}

      {allowedFor({ template: CAMPAIGN_FORM.AGENCY, status: campaignStatus }) && (
        <FormRow className='BasicsForm__row'>
          <SelectAsync
            id='agency'
            title={t('common.agency')}
            help={t('form.campaign.basics.agencyHelp')}
            placeholder={t('form.campaign.basics.agencyNamePlaceholder')}
            value={basicsValues.agency}
            options={BasicsFormService.getAgencies}
            onChange={(agency, id): void => {
              onInputChange(agency, id)
            }}
            errors={errors?.agency}
            disabled={disabledAgency()}
          />
        </FormRow>
      )}

      {allowedFor({ template: CAMPAIGN_FORM.AGENCY, status: campaignStatus }) &&
        basicsValues.agency?.rawData?.agencyType === AgencyType.external &&
        (allowedFor({
          feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.EditDiscount },
        }) ||
          allowedFor({ template: { roles: [BaseRole.ADMIN] } })) && (
          <FormRow className='BasicsForm__row'>
            <FormColumn>
              <Input
                id='agencyDiscount'
                title={t('form.campaign.basics.agencyDiscount')}
                placeholder={t('form.campaign.basics.agencyDiscountPlaceholder')}
                value={parseInt(basicsValues.agencyDiscount || '')}
                type={InputType.NUMBER}
                onChange={(input, id): void => {
                  const formattedInput = input.replace(',', '.')
                  onInputChange(formattedInput, id)
                }}
                errors={errors?.agencyDiscount}
                disabled={
                  campaignStatus !== CampaignStatus.SKETCH ||
                  !allowedFor({
                    feature: {
                      name: FeatureName.Campaigns,
                      action: CampaignPermissionType.EditDiscount,
                    },
                  })
                }
                suffix='%'
              />
            </FormColumn>
          </FormRow>
        )}

      {!userData.withAllocatedTime && (
        <FormRow className='BasicsForm__row'>
          <SelectAsync
            id='advertiser'
            title={t('common.advertiser')}
            help={t('form.campaign.basics.advertiserHelp')}
            placeholder={t('common.advertiser')}
            value={basicsValues.advertiser}
            isClearable={true}
            label={t('common.search') + '...'}
            options={BasicsFormService.getCompanies}
            onChange={(advertiser, id): void => onInputChange(advertiser, id)}
            asyncFiltering={true}
          />
        </FormRow>
      )}

      {basicsValues.dealId && (
        <FormRow className='BasicsForm__row'>
          <SelectAsync
            id='orderer'
            title={t('common.orderer')}
            help={t('form.campaign.basics.advertiserHelp')}
            placeholder={t('common.orderer')}
            value={basicsValues.orderer}
            isClearable={true}
            label={t('common.search') + '...'}
            options={BasicsFormService.getCompanies}
            onChange={(orderer, id): void => {
              onInputChange(orderer, id)
            }}
            asyncFiltering={true}
          />
        </FormRow>
      )}

      {allowedFor({ template: CAMPAIGN_FORM.SUPERVISORS, status: campaignStatus }) && (
        <FormRow className='BasicsForm__row'>
          <SelectAsync
            id='supervisors'
            title={t('common.supervisors')}
            placeholder={t('form.campaign.basics.supervisorsPlaceholder')}
            value={basicsValues.supervisors}
            onChange={onInputChange}
            options={BasicsFormService.getUsersForSupervisors}
            multi
            closeOnSelect={false}
            disabled={disabledSupervisors()}
          />
        </FormRow>
      )}

      <FormRow className='BasicsForm__row'>
        <SelectAsync
          id='tags'
          title={t('common.tags')}
          placeholder={t('form.campaign.basics.tagsPlaceholder')}
          value={(basicsValues.tags || []).map(
            (tag): SelectAsyncValue<string> => ({
              value: tag,
              label: tag,
              rawData: tag,
            })
          )}
          isClearable={true}
          multi={true}
          label={t('common.search') + '...'}
          options={BasicsFormService.getTags}
          onChange={(selectedTags: SelectAsyncValue<string>[]): void => {
            store.dispatch(
              campaignFormActions.updateBasicsValues({
                ...basicsValues,
                tags: selectedTags.map(tag => tag.value),
              })
            )
          }}
          creatable={true}
          tagTooltipContent={(tag: string) => <TagCampaignsTooltip tag={tag} />}
        />
      </FormRow>

      {basicsValues.dealId && (
        <FormRow className='BasicsForm__row'>
          <SelectAsync
            id='commercialSupervisor'
            title={t('common.commercialSupervisor')}
            placeholder={t('common.commercialSupervisor')}
            value={basicsValues.commercialSupervisor}
            isClearable={true}
            label={t('common.search') + '...'}
            options={BasicsFormService.getUsersForSupervisors}
            onChange={(commercialSupervisor, id): void => {
              onInputChange(commercialSupervisor, id)
            }}
            disabled={disabledSupervisors()}
          />
        </FormRow>
      )}

      {basicsValues.dealId && (
        <FormRow className='BasicsForm__row'>
          <SelectAsync
            id='department'
            title={t('common.department')}
            placeholder={t('common.department')}
            value={basicsValues.department}
            isClearable={true}
            label={t('common.search') + '...'}
            options={BasicsFormService.getDepartments}
            onChange={(department, id): void => {
              onInputChange(department, id)
            }}
            asyncFiltering={true}
          />
        </FormRow>
      )}

      {basicsValues.dealId && (
        <FormRow className='BasicsForm__row'>
          <FormColumn>
            <Input
              id='broadsignTotalOutdoor'
              title={t('form.campaign.basics.broadsignTotalOutdoor')}
              placeholder={t('form.campaign.basics.broadsignTotalOutdoorPlaceholder')}
              value={basicsValues.broadsignTotalOutdoor}
              onChange={(input, id): void => {
                // remove also any letters - we only want numbers and decimal points
                input = input.replace(/[^\d.,]/g, '')
                // This input field is a number with decimal places, so if it contains a comma,
                // we replace it with a dot to ensure correct formatting.
                const formattedInput = input.replace(',', '.')

                onInputChange(formattedInput, id)
              }}
              errors={errors?.broadsignTotalOutdoor}
            />
          </FormColumn>
        </FormRow>
      )}

      <FormRow className='BasicsForm__row'>
        <FormColumn className='BasicsForm__date-input--column'>
          <DatePickerSingle
            id='startDate'
            value={basicsValues.startDate}
            title={t('form.campaign.basics.startDate')}
            placeholder={DATE_PLACEHOLDER}
            isOutsideRange={day => {
              return isNotAllowedDate(
                day,
                basicsValues.endDate,
                basicsValues.startDate,
                true,
                basicsValues.reservationTill
              )
            }}
            onChange={onInputChange}
            errors={errors?.startDate}
            disabled={!allowedFor({ template: CAMPAIGN_FORM.START_DATE, status: campaignStatus })}
          />
        </FormColumn>

        <FormColumn className='BasicsForm__date-input--column'>
          <DatePickerSingle
            id='endDate'
            value={basicsValues.endDate}
            title={t('form.campaign.basics.endDate')}
            placeholder={DATE_PLACEHOLDER}
            isOutsideRange={day =>
              isNotAllowedDate(day, basicsValues.endDate, basicsValues.startDate, false)
            }
            onChange={onInputChange}
            errors={errors?.endDate}
            disabled={!allowedFor({ template: CAMPAIGN_FORM.END_DATE, status: campaignStatus })}
          />
        </FormColumn>
      </FormRow>
    </div>
  )
}

export default BasicsForm
